import { css } from "@emotion/react";
import { type ReactNode } from "react";

import getEnumValues from "@mpa/toolbox/enums/getEnumValues";
import type { Projet } from "@mpa/toolbox/models/Projet";
import type { UserCredential, UserLogin } from "@mpa/toolbox/models/User";

import type { Action } from "../../action/Action";
import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import Centered from "../../components/Centered";
import WithContextualMenu from "../../components/ContextualMenu/WithContextualMenu";
import { useDialogConfirm } from "../../components/dialog/useDialogConfirm";
import Divider from "../../components/Divider";
import EmptyView from "../../components/EmptyView";
import type { IconName } from "../../components/Icon/types";
import List from "../../components/List/List";
import ListItem from "../../components/List/ListItem";
import LightPageLayout from "../../components/Page/LightPageLayout";
import PageHeader from "../../components/Page/PageHeader";
import Spacer from "../../components/space/Spacer";
import { getSpaceVariable } from "../../components/space/utilities";
import Text from "../../components/Text";
import { clearSelectedProjet, useSelectedProjetId } from "../../data/selectedProjet";
import { callOutgoingRequest, useOutgoingRequest } from "../../data/tanstack-query";
import {
    deleteUser,
    deleteUserProject,
    detachUserProject,
    getUser,
    invalidateLogin,
    invalidateUserLogins,
    updateUser,
} from "../../endpoints/outgoing-requests";
import { CredentialType } from "../../enums/CredentialType";
import { Permission, PermissionInfo } from "../../enums/Permission";
import useSubmit from "../../hooks/useSubmit";
import { usePathParams } from "../../navigation/hooks";
import { goBack } from "../../navigation/location";
import { userRoute } from "./navigation";

export default function UserPage() {
    const { user: id } = usePathParams(userRoute);
    const user = useOutgoingRequest(getUser(id));
    const title = user.credentials[0]?.key ?? user.id;
    const [onDelete, deletion] = useSubmit(async () => {
        await callOutgoingRequest(deleteUser(id, user.self));
        if (!user.self) goBack();
    }, [id, user.self]);
    const [onInvalidate, invalidation] = useSubmit(
        async (login: UserLogin) => {
            await callOutgoingRequest(invalidateLogin(id, login.id, login.current));
        },
        [id],
    );
    const hasActiveLogins = user.logins.some((login) => login.invalidatedAt === null);
    const includesCurrentLogin = user.logins.some(
        (login) => login.current && login.invalidatedAt === null,
    );
    const [onInvalidateAll, invalidateAll] = useSubmit(async () => {
        await callOutgoingRequest(invalidateUserLogins(id, includesCurrentLogin));
    }, [id, includesCurrentLogin]);
    const [showInvalidateAllDialog, invalidateAllConfirmNode] = useDialogConfirm({
        message: "Confirmation de résiliation de toutes les sessions",
        confirmLabel: "Résilier",
        onConfirm: onInvalidateAll,
    });
    const [onUpdatePermissions, permissionsUpdate] = useSubmit(
        async (permissions: Permission[]) => {
            await callOutgoingRequest(updateUser(id, permissions));
        },
        [id],
    );
    const selectedProjetId = useSelectedProjetId();
    const [onDeleteProjet, projetDeletion] = useSubmit(
        async (projetId: string) => {
            await callOutgoingRequest(deleteUserProject(id, projetId));
            if (projetId === selectedProjetId) clearSelectedProjet();
        },
        [id, selectedProjetId],
    );
    const [onDetachProjet, projetDetach] = useSubmit(
        async (projetId: string) => {
            await callOutgoingRequest(detachUserProject(id, projetId));
            if (user.self && projetId === selectedProjetId) clearSelectedProjet();
        },
        [id, user.self, selectedProjetId],
    );

    return (
        <LightPageLayout>
            <PromiseToast promise={deletion} message="Utilisateur supprimé" />
            <PromiseToast promise={invalidation} message="Session résiliée" />
            <PromiseToast promise={invalidateAll} message="Sessions résiliées" />
            <PromiseToast promise={permissionsUpdate} message="Permissions mises à jour" />
            <PromiseToast promise={projetDeletion} message="Projet supprimé" />
            <PromiseToast promise={projetDetach} message="Projet détaché" />
            <PageHeader title={user.self ? `${title} (vous)` : title} />
            <Section title="Permissions">
                <List
                    items={[...getEnumValues(Permission)]}
                    keyExtractor={(permission) => permission}
                    children={(permission) => (
                        <PermissionRow
                            permission={permission}
                            checked={user.permissions.includes(permission)}
                            onToggle={(enabled) => {
                                const next = new Set(user.permissions);
                                if (enabled) next.add(permission);
                                else next.delete(permission);
                                onUpdatePermissions([...next]);
                            }}
                        />
                    )}
                />
            </Section>
            <Spacer scale={8} />
            <Section title="Méthodes de connexion">
                {user.credentials.length === 0 ? (
                    <EmptyView message="Aucune méthode de connexion" />
                ) : (
                    <List
                        items={user.credentials}
                        keyExtractor={(credential) => `${credential.type}:${credential.key}`}
                        children={(credential) => (
                            <ListItem
                                icon={credentialIcon(credential)}
                                label={credential.key}
                                help={credentialLabel(credential)}
                            />
                        )}
                    />
                )}
            </Section>
            <Spacer scale={8} />
            <WithContextualMenu
                label="Toutes les sessions"
                items={
                    hasActiveLogins
                        ? [
                              {
                                  label: "Résilier",
                                  icon: "cross",
                                  action: showInvalidateAllDialog,
                                  dangerous: true,
                              },
                          ]
                        : []
                }
            >
                <Section title="Sessions">
                    {user.logins.length === 0 ? (
                        <EmptyView message="Aucune session" />
                    ) : (
                        <List
                            items={user.logins}
                            keyExtractor={(login) => login.id}
                            children={(login) => (
                                <LoginRow login={login} onInvalidate={onInvalidate} />
                            )}
                        />
                    )}
                </Section>
            </WithContextualMenu>
            {invalidateAllConfirmNode}
            <Spacer scale={8} />
            <Section title="Projets">
                {user.projets.length === 0 ? (
                    <EmptyView message="Aucun projet" />
                ) : (
                    <List
                        items={user.projets}
                        keyExtractor={(projet) => projet.id}
                        children={(projet) => (
                            <ProjetRow
                                projet={projet}
                                onDelete={onDeleteProjet}
                                onDetach={onDetachProjet}
                            />
                        )}
                    />
                )}
            </Section>
            <DeleteZone label="Supprimer cet utilisateur" onDelete={onDelete} />
        </LightPageLayout>
    );
}

function Section(props: { title: string; children: ReactNode }) {
    const { title, children } = props;
    return (
        <div>
            <Text variant="heading3">{title}</Text>
            <Spacer scale={3} />
            {children}
        </div>
    );
}

function PermissionRow(props: {
    permission: Permission;
    checked: boolean;
    onToggle: (enabled: boolean) => void;
}) {
    const { permission, checked, onToggle } = props;
    const copy = PermissionInfo[permission];
    return (
        <label
            css={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: getSpaceVariable(3),
            })}
        >
            <div css={css({ flex: 1, minWidth: 0 })}>
                <ListItem label={copy.label} help={copy.help} />
            </div>
            <div css={{ paddingRight: getSpaceVariable(4) }}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => onToggle(event.target.checked)}
                />
            </div>
        </label>
    );
}

function LoginRow(props: { login: UserLogin; onInvalidate: (login: UserLogin) => void }) {
    const { login, onInvalidate } = props;
    const [showDialog, confirmNode] = useDialogConfirm({
        message: "Confirmation de résiliation",
        confirmLabel: "Résilier",
        onConfirm: () => onInvalidate(login),
    });
    const status = login.invalidatedAt
        ? `Résiliée · ${formatDate(login.invalidatedAt)}`
        : login.current
          ? "Session actuelle"
          : `Dernière activité · ${formatDate(login.lastUsedAt)}`;
    return (
        <>
            <WithContextualMenu
                label={`Session ${login.device}`}
                items={
                    login.invalidatedAt === null
                        ? [
                              {
                                  label: "Résilier",
                                  icon: "cross",
                                  action: showDialog,
                                  dangerous: true,
                              },
                          ]
                        : []
                }
            >
                <ListItem
                    label={login.device}
                    help={`${status} · créée le ${formatDate(login.createdAt)}`}
                />
            </WithContextualMenu>
            {confirmNode}
        </>
    );
}

function ProjetRow(props: {
    projet: Projet;
    onDelete: (projetId: string) => void;
    onDetach: (projetId: string) => void;
}) {
    const { projet, onDelete, onDetach } = props;
    const [showDetachDialog, detachConfirmNode] = useDialogConfirm({
        message: "Confirmation de détachement",
        confirmLabel: "Détacher",
        onConfirm: () => onDetach(projet.id),
    });
    const [showDeleteDialog, deleteConfirmNode] = useDialogConfirm({
        message: "Confirmation de suppression",
        onConfirm: () => onDelete(projet.id),
    });
    return (
        <>
            <WithContextualMenu
                label={projet.label}
                items={[
                    {
                        label: "Détacher",
                        action: showDetachDialog,
                    },
                    {
                        label: "Supprimer",
                        icon: "cross",
                        action: showDeleteDialog,
                        dangerous: true,
                    },
                ]}
            >
                <ListItem label={projet.label} help={formatDate(projet.createdAt)} />
            </WithContextualMenu>
            {detachConfirmNode}
            {deleteConfirmNode}
        </>
    );
}

function credentialIcon(credential: UserCredential): IconName {
    if (credential.type === CredentialType.Email) return "email";
    if (credential.type === CredentialType.Anonymous) return "anonymous";
    return "users";
}

function credentialLabel(credential: UserCredential) {
    if (credential.type === CredentialType.Email) return "Email";
    if (credential.type === CredentialType.Anonymous) return "Anonyme";
    return credential.type;
}

function formatDate(value: Date | string) {
    return new Date(value).toLocaleString("fr-FR");
}

type DeleteZoneProps = {
    label: string;
    onDelete: Action;
};

function DeleteZone(props: DeleteZoneProps) {
    const { label, onDelete } = props;
    const [showDialog, confirmNode] = useDialogConfirm({
        message: "Confirmation de suppression",
        onConfirm: onDelete,
    });

    return (
        <>
            <Spacer scale={9} />
            <Divider />
            <Spacer scale={9} />
            <Centered>
                <Button label={label} action={showDialog} feeling="danger" role="minor" />
            </Centered>
            {confirmNode}
        </>
    );
}
